import type {
  TenantAnalyticsAdapter,
  TenantAnalyticsEnvelope,
  TenantAnalyticsItem,
  TenantBeginCheckoutEvent,
  TenantPageViewEvent,
  TenantPurchaseEvent,
  TenantViewContentEvent,
} from '../types';
import {
  type GoogleAnalytics4PublicConfig,
  parseGoogleAnalytics4PublicConfig,
} from './config';

interface GoogleAnalytics4EventParametersBase {
  readonly event_id: string;
  readonly send_to: string;
}

export interface GoogleAnalytics4PageViewParameters extends GoogleAnalytics4EventParametersBase {
  readonly page_location: string;
  readonly page_title?: string;
  readonly page_referrer?: string;
}

export interface GoogleAnalytics4Item {
  readonly item_id: string;
  readonly item_name?: string;
  readonly item_category: string;
  readonly quantity: number;
  readonly price?: number;
}

export interface GoogleAnalytics4ViewItemParameters extends GoogleAnalytics4EventParametersBase {
  readonly items: readonly GoogleAnalytics4Item[];
  readonly value?: number;
  readonly currency?: string;
}

export interface GoogleAnalytics4PurchaseParameters extends GoogleAnalytics4EventParametersBase {
  readonly transaction_id: string;
  readonly value: number;
  readonly currency: string;
  readonly items: readonly GoogleAnalytics4Item[];
}

export type GoogleAnalytics4EventName =
  | 'page_view'
  | 'view_item'
  | 'begin_checkout'
  | 'purchase';
export type GoogleAnalytics4EventParameters =
  | GoogleAnalytics4PageViewParameters
  | GoogleAnalytics4ViewItemParameters
  | GoogleAnalytics4PurchaseParameters;

export type GoogleAnalytics4EventSink = (
  eventName: GoogleAnalytics4EventName,
  parameters: GoogleAnalytics4EventParameters,
) => void | Promise<void>;

export interface CreateGoogleAnalytics4AdapterOptions {
  readonly publicConfig: unknown;
  readonly pageOrigin: string;
  readonly send: GoogleAnalytics4EventSink;
  readonly id?: string;
  readonly enabled?: boolean;
}

export function createGoogleAnalytics4Adapter(
  options: CreateGoogleAnalytics4AdapterOptions,
): TenantAnalyticsAdapter {
  const publicConfig = requirePublicConfig(options.publicConfig);
  const pageOrigin = requirePageOrigin(options.pageOrigin);

  if (typeof options.send !== 'function') {
    throw new Error('Google Analytics 4 requires an event sink.');
  }

  const send = options.send;

  return Object.freeze({
    id: options.id ?? `google_analytics_4:${publicConfig.measurementId}`,
    provider: 'google_analytics_4',
    requiredConsent: Object.freeze(['analytics'] as const),
    enabled: options.enabled !== false,
    deliver: async (envelope: TenantAnalyticsEnvelope) => {
      await deliverEvent(envelope, publicConfig, pageOrigin, send);
    },
  });
}

async function deliverEvent(
  envelope: TenantAnalyticsEnvelope,
  publicConfig: GoogleAnalytics4PublicConfig,
  pageOrigin: string,
  send: GoogleAnalytics4EventSink,
) {
  switch (envelope.event.name) {
    case 'page_view':
      await send(
        'page_view',
        mapPageView(envelope.event, publicConfig, pageOrigin),
      );
      return;
    case 'view_content':
      await send('view_item', mapViewContent(envelope.event, publicConfig));
      return;
    // GA4's recommended checkout-entry event; reuses the view_item parameter
    // shape (single item, optional value/currency).
    case 'begin_checkout':
      await send(
        'begin_checkout',
        mapViewContent(envelope.event, publicConfig),
      );
      return;
    case 'purchase':
      await send('purchase', mapPurchase(envelope.event, publicConfig));
      return;
  }
}

function mapPageView(
  event: TenantPageViewEvent,
  publicConfig: GoogleAnalytics4PublicConfig,
  pageOrigin: string,
): GoogleAnalytics4PageViewParameters {
  return Object.freeze({
    event_id: event.eventId,
    send_to: publicConfig.measurementId,
    page_location: new URL(event.path, pageOrigin).toString(),
    ...(event.title ? { page_title: event.title } : {}),
    ...(event.referrer
      ? { page_referrer: new URL(event.referrer, pageOrigin).toString() }
      : {}),
  });
}

function mapViewContent(
  event: TenantViewContentEvent | TenantBeginCheckoutEvent,
  publicConfig: GoogleAnalytics4PublicConfig,
): GoogleAnalytics4ViewItemParameters {
  const item = Object.freeze({
    item_id: event.contentId,
    ...(event.contentName ? { item_name: event.contentName } : {}),
    item_category: event.contentType,
    quantity: 1 as const,
    ...(event.value !== undefined ? { price: event.value } : {}),
  });

  return Object.freeze({
    event_id: event.eventId,
    send_to: publicConfig.measurementId,
    items: Object.freeze([item]),
    ...(event.value !== undefined
      ? { value: event.value, currency: event.currency }
      : {}),
  });
}

function mapPurchase(
  event: TenantPurchaseEvent,
  publicConfig: GoogleAnalytics4PublicConfig,
): GoogleAnalytics4PurchaseParameters {
  return Object.freeze({
    event_id: event.eventId,
    send_to: publicConfig.measurementId,
    // GA4 deduplicates purchase reporting on transaction_id, so replays of the
    // same verified purchase (page refresh, revisit) collapse to one purchase.
    transaction_id: event.transactionId,
    value: event.value,
    currency: event.currency,
    items: Object.freeze(
      event.items.map((item) => mapPurchaseItem(item, event.name)),
    ),
  });
}

function mapPurchaseItem(item: TenantAnalyticsItem, fallbackCategory: string) {
  return Object.freeze({
    item_id: item.itemId,
    ...(item.itemName ? { item_name: item.itemName } : {}),
    item_category: item.category ?? fallbackCategory,
    quantity: item.quantity,
    price: item.price,
  });
}

function requirePublicConfig(value: unknown): GoogleAnalytics4PublicConfig {
  const publicConfig = parseGoogleAnalytics4PublicConfig(value);

  if (!publicConfig) {
    throw new Error('Invalid Google Analytics 4 public configuration.');
  }

  return publicConfig;
}

function requirePageOrigin(value: unknown) {
  if (typeof value !== 'string') {
    throw new Error('Google Analytics 4 requires a valid page origin.');
  }

  try {
    const url = new URL(value);

    if (
      (url.protocol !== 'https:' && url.protocol !== 'http:') ||
      url.username ||
      url.password ||
      url.pathname !== '/' ||
      url.search ||
      url.hash ||
      url.origin !== value
    ) {
      throw new Error('invalid origin');
    }

    return url.origin;
  } catch {
    throw new Error('Google Analytics 4 requires a valid page origin.');
  }
}
